// Analytics/tabs/RadiologyTab/index.tsx
import React from 'react';
import { Grid, Card, CardHeader, CardContent, Box } from '@mui/material';
import { useRadiologyData } from '../../hooks/useRadiologyData';
import { analyticsCharts } from '../../config/analyticsCharts';
import LoadingState from '../../common/LoadingState';
import ErrorState from '../../common/ErrorState';
import ChartFactory from '../../charts/ChartFactory';
import { IDepartment } from '../../../../../constants/types';

interface ChartConfig {
  id: string;
  source: string;
  order: number;
  title: string;
  type: 'bar' | 'line' | 'pie' | 'doughnut' | 'gauge' | 'demographic' | 'data-only' | 'data-value';
  cols: number;
  visible: boolean;
  debug?: boolean;
}

interface RadiologyTabProps {
  investigation: {
    permissions: string[];
    [key: string]: any;
  };
  departmentSelected: string;
  startDate: number | null;
  endDate: number | null;
  departments: IDepartment[];
}

const RadiologyTab: React.FC<RadiologyTabProps> = ({ 
  investigation, 
  departmentSelected, 
  startDate, 
  endDate, 
  departments,
}) => {
  const outpatientsCharts = analyticsCharts.radiology as ChartConfig[] || [];
  
  const visibleCharts = outpatientsCharts
    .filter(chart => chart.visible)
    .sort((a, b) => a.order - b.order);

  const debugCharts = outpatientsCharts
    .filter(chart => chart.debug)
    .sort((a, b) => a.order - b.order);

  return (
    <>
      <Grid container spacing={3}>
        {visibleCharts.map((chart) => (
          <Grid 
            item 
            xs={12} 
            sm={chart.cols === 12 ? 12 : 6} 
            md={chart.cols} 
            key={chart.id}
          >
            <ChartWrapper
              chart={chart}
              investigation={investigation}
              departmentSelected={departmentSelected}
              startDate={startDate}
              endDate={endDate}
              departments={departments}
            />
          </Grid>
        ))} 
      </Grid>
      <>
        {debugCharts.map((chart) => (
          <DebugChartWrapper
            key={chart.id}
            chart={chart}
            investigation={investigation}
            departmentSelected={departmentSelected}
            startDate={startDate}
            endDate={endDate}
          />
        ))}
      </>
    </>
  );
};

interface ChartWrapperProps {
  chart: ChartConfig;
  investigation: RadiologyTabProps['investigation'];
  departmentSelected: string;
  startDate: number | null;
  endDate: number | null;
  departments: IDepartment[];
}

const ChartWrapper: React.FC<ChartWrapperProps> = ({
  chart,
  investigation,
  departmentSelected,
  startDate,
  endDate,
  departments,
}) => {
  const { data, loading, error } = useRadiologyData({
    investigation,
    departmentSelected,
    startDate: startDate?.toString() || '',
    endDate: endDate?.toString() || '',
    requestedMethod: chart.source as any
  });

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  return (
    <ChartFactory
      id={chart.id}
      type={chart.type}
      title={chart.title}
      data={data}
      departments={departments}
      departmentSelected={departments && departments.length > 0 ? departments[0] : null}
    />
  );
};

const DebugChartWrapper: React.FC<Omit<ChartWrapperProps, 'departments'>> = ({
  chart,
  investigation,
  departmentSelected,
  startDate,
  endDate,
}) => {
  const { data, loading, error } = useRadiologyData({
    investigation,
    departmentSelected,
    startDate: startDate?.toString() || '',
    endDate: endDate?.toString() || '',
    requestedMethod: chart.source as any
  });

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  return (
    <Card>
      <CardHeader title={chart.id} subheader={chart.source} />
      <CardContent>
        <Box>
          {data && (
            <pre style={{ 
              overflow: 'auto', 
              maxHeight: '100%',
              margin: 0,
              padding: '1rem',
              background: '#f5f5f5',
              borderRadius: '4px'
            }}>
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RadiologyTab;